import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { Notification, type NotificationType } from './index';

interface NotificationContextType {
  showNotification: (
    message: string | string[],
    type: NotificationType
  ) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [notification, setNotification] = useState<{
    message: string | string[];
    type: NotificationType;
    visible: boolean;
  }>({ message: '', type: 'success', visible: false });

  const showNotification = useCallback(
    (message: string | string[], type: NotificationType) => {
      setNotification({
        message,
        type,
        visible: true,
      });
    },
    []
  );

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (notification.visible && notification.type === 'success') {
      timer = setTimeout(() => {
        setNotification(n => ({ ...n, visible: false }));
      }, 3000);
    }
    return () => clearTimeout(timer);
  }, [notification.visible, notification.type]);

  const handleClose = () => {
    setNotification(n => ({ ...n, visible: false }));
  };

  return (
    <NotificationContext.Provider value={{ showNotification }}>
      {children}
      {notification.visible && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={handleClose}
        />
      )}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      'useNotification must be used within a NotificationProvider'
    );
  }
  return context.showNotification;
};
